import { NgClass } from '@angular/common';
import { Component, inject, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';

import { ApiService, ConfigService } from '@app-core/services';
import { OverlayRef } from '@app-core/services/overlay';
import { EmailValidator, RequiredValidator } from '@app-core/validations';
import { DealerInfoResponse } from '@app-model/api';
import { IRequestAccountRequest } from '@app-model/request';
import { ButtonComponent } from '@app-shared/components/button/button.component';
import { IconComponent } from '@app-shared/components/icon/icon.component';
import { InputControlComponent } from '@app-shared/components/input-control/input-control.component';
import { SpinnerComponent } from '@app-shared/components/spinner/spinner.component';
import { RecaptchaComponent } from '@app-shared/recaptcha/recaptcha.component';
import { untilDestroyed } from '@app-shared/utils';
import { of, throwError } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';

interface IRequestForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  title: FormControl<string>;
  phone: FormControl<string>;
  email: FormControl<string>;
  dealer: FormControl<string>;
  captcha: FormControl<string>;
  dealerInfo: FormGroup<IDealerInfoForm>;
}

interface IDealerInfoForm {
  dealerAddress1: FormControl<string>;
  dealerCity: FormControl<string>;
  dealerFullName: FormControl<string>;
  dealerName: FormControl<string>;
  dealerPhone: FormControl<string>;
  dealerState: FormControl<string>;
  dealerZipCode: FormControl<string>;
  cmsDealerNumber: FormControl<string>;
  countryCode: FormControl<string>;
  status: FormControl<boolean>;
}

@Component({
  templateUrl: './request-account-popup.component.html',
  styleUrls: ['./request-account-popup.component.scss'],
  imports: [
    ButtonComponent,
    IconComponent,
    InputControlComponent,
    NgClass,
    RecaptchaComponent,
    ReactiveFormsModule,
    SpinnerComponent,
  ],
})
export class RequestAccountPopupComponent {
  private ref = inject<OverlayRef<RequestAccountPopupComponent>>(OverlayRef);
  private fb = inject(NonNullableFormBuilder);
  private apiService = inject(ApiService);
  private _configService = inject(ConfigService);

  @ViewChild(RecaptchaComponent) recaptchaRef: RecaptchaComponent | undefined;

  requestForm: FormGroup<IRequestForm>;
  emailSuccess = signal(false);
  searching = signal(false);
  dealerDecoded = signal(false);
  searchSuccess = signal(false);

  readonly #untilDestroyed = untilDestroyed();

  constructor() {
    this.requestForm = this.fb.group({
      firstName: this.fb.control('', [RequiredValidator]),
      lastName: this.fb.control('', [RequiredValidator]),
      title: this.fb.control('', [RequiredValidator]),
      phone: this.fb.control('', [RequiredValidator]),
      email: this.fb.control('', [RequiredValidator, EmailValidator]),
      dealer: this.fb.control('', [RequiredValidator]),
      captcha: this.fb.control('', [RequiredValidator]),
      dealerInfo: this.fb.group({
        dealerAddress1: this.fb.control({ value: '', disabled: true }),
        dealerCity: this.fb.control({ value: '', disabled: true }),
        dealerFullName: this.fb.control({ value: '', disabled: true }),
        dealerName: this.fb.control({ value: '', disabled: true }, [RequiredValidator]),
        dealerPhone: this.fb.control({ value: '', disabled: true }),
        dealerState: this.fb.control({ value: '', disabled: true }),
        dealerZipCode: this.fb.control({ value: '', disabled: true }),
        cmsDealerNumber: this.fb.control({ value: '', disabled: true }),
        countryCode: this.fb.control({ value: '', disabled: true }),
        status: this.fb.control({ value: false, disabled: true }),
      }),
    });
  }

  verifyDealer(): void {
    this.requestForm.get('dealerInfo').reset();
    const cmsDealerNumber = this.requestForm.get('dealer').value;
    this.searching.set(true);
    this.searchSuccess.set(false);
    this.apiService
      .getDealer({ application: this._configService.appIdentifier, cmsDealerNumber })
      .pipe(
        catchError(() => {
          this.searching.set(false);
          this.dealerDecoded.set(true);
          this.searchSuccess.set(false);

          return of(null);
        }),
        this.#untilDestroyed()
      )
      .subscribe((data: DealerInfoResponse) => {
        if (data) {
          this.requestForm.get('dealerInfo').patchValue(data);
          this.requestForm.get('dealer').disable();
        }
        this.searching.set(false);
        this.dealerDecoded.set(true);
        this.searchSuccess.set(!!data);
      });
  }

  resetDealer(): void {
    this.requestForm.get('dealer').enable();
    this.requestForm.get('dealer').reset();
    this.requestForm.get('dealerInfo').reset();
    this.searching.set(false);
    this.searchSuccess.set(false);
    this.dealerDecoded.set(false);
  }

  requestAccount(): void {
    const { email, firstName, lastName, title, phone, dealer, dealerInfo, captcha } = this.requestForm.getRawValue();

    const request: IRequestAccountRequest = {
      application: this._configService.appIdentifier,
      email,
      firstName,
      lastName,
      title,
      phone,
      dealer,
      dealerName: dealerInfo.dealerName,
      captcha,
      captchaVersion: 'v3',
    };
    this.apiService
      .requestAccount(request)
      .pipe(
        filter((data: { status: boolean }) => data?.status),
        catchError((err: Error) => {
          this.requestForm.get('captcha').reset();
          this.recaptchaRef?.execute();

          return throwError(() => err);
        }),
        this.#untilDestroyed()
      )
      .subscribe(() => {
        this.emailSuccess.set(true);
        this.requestForm.reset();
      });
  }

  close(): void {
    this.ref.close();
  }

  onCaptchaResolved(token: string) {
    this.requestForm.get('captcha')?.setValue(token);
  }
}
